/**
 * Aus einer fertig beschrifteten Tabelle wird eine Excel-Mappe
 * (2.13 Abschnitt 9.11).
 *
 * **Der Bildschirm schreibt die Sätze, der Server packt die Datei.** Die
 * Erläuterung einer Buchung ist ein Textbaustein mit eingesetzten Werten, und
 * zusammengesetzt wird er hier — in der Sprachdatei (R157), dort, wo auch der
 * Bildschirm ihn zusammensetzt. Täte der Server es ein zweites Mal, gäbe es
 * zwei Fassungen desselben Satzes, und eines Tages gingen sie auseinander.
 *
 * **Diese Stelle gehört keinem Bericht.** Die nächste Ausgabe nach Excel
 * benutzt dieselbe — sonst steht in einem halben Jahr an fünf Bildschirmen je
 * ein eigener Mappenbauer.
 */
import { Betriebsstoerung, gemeldet, stoerungAus } from './api'
import { rufe } from './Stoerungsstelle'

/** Eine Zelle: Text **oder** Zahl, nie beides. */
export type Mappenzelle = { text?: string; zahl?: number; ganz?: boolean }

export type Mappenblatt = {
  name: string
  /** Die Zeilen über dem Tabellenkopf — woher die Mappe stammt. */
  briefkopf: string[]
  kopf: string[]
  zeilen: Mappenzelle[][]
}

export type Mappenauftrag = { dateiname: string; blaetter: Mappenblatt[] }

/**
 * Ein Text in einer Zelle.
 *
 * **Leer statt „—".** Ein Gedankenstrich ist am Bildschirm die Auskunft
 * „hier steht nichts"; in einer Tabelle, in der jemand filtert, ist er ein
 * Wert wie jeder andere.
 */
export const text = (wert: string | null | undefined): Mappenzelle => ({ text: wert ?? '' })

/**
 * Ein Betrag.
 *
 * **Als Zahl und nicht als Zeichenkette**: Wer einen Betrag als Text schreibt,
 * liefert eine Datei, in der ein Prüfer nicht summieren kann — und genau das
 * will er tun (2.12 Abschnitt 3.6).
 */
export const zahl = (wert: number): Mappenzelle => ({ zahl: wert })

/**
 * Eine Anzahl oder eine Periode — ohne Nachkommastellen.
 *
 * „202512,00" wäre nicht falsch, sondern irreführend: Es sieht aus wie ein
 * Betrag und ist eine Kennung.
 */
export const ganz = (wert: number): Mappenzelle => ({ zahl: wert, ganz: true })

/**
 * Holt die Mappe und übergibt sie dem Browser.
 *
 * **Ein Knopf und kein Verweis**, weil die Tabelle in den Rumpf der Anfrage
 * gehört und nicht in eine Adresse: Die Buchungen des Beispielkonzerns sind
 * einige hundert Zeilen, und eine Adresse ist dafür der falsche Ort.
 */
export async function ladeMappe(auftrag: Mappenauftrag): Promise<void> {
  // **Eine Störung endet im Störungsfenster** (7.2 Abschnitt 30, R1003) —
  // derselbe Weg wie bei jedem anderen Abruf, obwohl dieser eine Datei holt.
  let antwort: Response
  try {
    antwort = await rufe('/api/mappe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(auftrag),
    })
  } catch {
    throw gemeldet(new Betriebsstoerung('meldung.stoerung.unerreichbar'))
  }

  if (!antwort.ok) {
    throw await stoerungAus(antwort)
  }

  const datei = await antwort.blob()
  const adresse = URL.createObjectURL(datei)

  try {
    const verweis = document.createElement('a')
    verweis.href = adresse
    verweis.download = auftrag.dateiname
    document.body.append(verweis)
    verweis.click()
    verweis.remove()
  } finally {
    // **Sonst bleibt die Datei im Speicher des Browsers liegen**, solange die
    // Seite offen ist -- und eine Mappe mit ein paar hundert Buchungen ist
    // nicht klein.
    URL.revokeObjectURL(adresse)
  }
}
